<script setup lang="ts">
import { computed, defineAsyncComponent, reactive, ref, watch } from "vue";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  LayoutTemplate,
  UsersRound,
  X,
} from "lucide-vue-next";
import { useRouter } from "vue-router";
import { useWorkspaceStore } from "@/stores/workspace";
import { addDays, todayLocalDate } from "@/utils/dates";
import {
  STANDARD_PROJECT_SECTIONS,
  type RichTextDocument,
} from "@/types/models";
import { emptyRichTextDocument } from "@/utils/rich-text";

const props = defineProps<{ open: boolean; initialTemplateId?: string }>();
const RichTextEditor = defineAsyncComponent(
  () => import("@/components/content/RichTextEditor.vue"),
);
const emit = defineEmits<{ close: [] }>();
const router = useRouter();
const workspace = useWorkspaceStore();
const step = ref(1);
const saving = ref(false);
const error = ref("");
const descriptionValid = ref(true);
const form = reactive({
  name: "",
  description: "",
  descriptionRichText: emptyRichTextDocument() as RichTextDocument,
  clientOrganizationId: "",
  startDate: todayLocalDate(),
  targetEndDate: addDays(todayLocalDate(), 90),
  weekdaysOnly: true,
  timezone: "America/Toronto",
  startingPoint: "blank",
});
const steps = [
  "Project details",
  "People and organizations",
  "Starting point",
  "Review",
];
const canAdvance = computed(
  () =>
    step.value !== 1 ||
    (!!form.name &&
      !!form.startDate &&
      !!form.targetEndDate &&
      descriptionValid.value),
);
const selectedOrganization = computed(() =>
  workspace.organizations.find(
    (organization) => organization.id === form.clientOrganizationId,
  ),
);
const selectedTemplate = computed(() =>
  workspace.templates.find((template) => template.id === form.startingPoint),
);

watch(
  () => props.open,
  (open) => {
    if (!open) return;
    form.startDate = todayLocalDate();
    form.targetEndDate = addDays(form.startDate, 90);
    form.weekdaysOnly = true;
    form.startingPoint = props.initialTemplateId || "blank";
    form.descriptionRichText = emptyRichTextDocument();
    void Promise.all([
      workspace.loadOrganizations(),
      workspace.loadTemplates(),
    ]);
  },
  { immediate: true },
);

async function create() {
  saving.value = true;
  error.value = "";
  try {
    const project = await workspace.createProject({
      ...form,
      workingWeekdays: form.weekdaysOnly
        ? [1, 2, 3, 4, 5]
        : [1, 2, 3, 4, 5, 6, 7],
      client: selectedOrganization.value?.name || "No client organization",
      ...(form.startingPoint !== "blank"
        ? { templateId: form.startingPoint }
        : {}),
    });
    emit("close");
    step.value = 1;
    router.push(`/projects/${project.id}/list`);
  } catch (createError) {
    error.value =
      createError instanceof Error
        ? createError.message
        : "The project could not be created. Your setup choices are still here.";
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="flow-layer">
      <button
        class="flow-scrim"
        type="button"
        aria-label="Close project setup"
        @click="emit('close')"
      ></button>
      <section
        class="flow"
        role="dialog"
        aria-modal="true"
        aria-labelledby="flow-title"
      >
        <header class="flow__header">
          <div>
            <h2 id="flow-title">Create project</h2>
            <p v-if="selectedTemplate">
              Starting with {{ selectedTemplate.name }}. Add the project
              details, then review the imported structure.
            </p>
            <p v-else>Build a clear starting point for the team and client.</p>
          </div>
          <button
            class="icon-button"
            type="button"
            aria-label="Close project setup"
            @click="emit('close')"
          >
            <X :size="20" />
          </button>
        </header>
        <div class="flow__layout">
          <nav class="flow__steps" aria-label="Project setup steps">
            <button
              v-for="(label, index) in steps"
              :key="label"
              type="button"
              :class="{ current: step === index + 1, done: step > index + 1 }"
              :aria-current="step === index + 1 ? 'step' : undefined"
              @click="index + 1 < step ? (step = index + 1) : undefined"
            >
              <span
                ><Check v-if="step > index + 1" :size="14" />{{
                  step <= index + 1 ? index + 1 : ""
                }}</span
              >{{ label }}
            </button>
          </nav>
          <div class="flow__body">
            <p v-if="error" class="flow-error" role="alert">{{ error }}</p>
            <section v-if="step === 1" class="flow-section">
              <div>
                <h3>Project details</h3>
                <p>Set the scope and dates participants will plan against.</p>
              </div>
              <div class="field">
                <label for="project-name">Project name</label
                ><input
                  id="project-name"
                  v-model="form.name"
                  class="input"
                  autocomplete="off"
                  placeholder="Example: Client implementation"
                  required
                />
              </div>
              <div class="field">
                <span class="field-label">Description</span>
                <RichTextEditor
                  v-model="form.descriptionRichText"
                  label="Project description"
                  :maxlength="2000"
                  placeholder="What is this project expected to deliver?"
                  @update:plain-text="form.description = $event"
                  @update:valid="descriptionValid = $event"
                />
                <p class="helper">
                  You can paste formatted Word content now. Paste images after
                  creating the project so they can enter its secure file area.
                </p>
              </div>
              <div class="form-row">
                <div class="field">
                  <label for="project-start">Start date</label
                  ><input
                    id="project-start"
                    v-model="form.startDate"
                    class="input"
                    type="date"
                  />
                </div>
                <div class="field">
                  <label for="project-end">Project target</label
                  ><input
                    id="project-end"
                    v-model="form.targetEndDate"
                    class="input"
                    type="date"
                  />
                </div>
              </div>
              <label class="workweek-option" for="create-weekdays-only">
                <input
                  id="create-weekdays-only"
                  v-model="form.weekdaysOnly"
                  type="checkbox"
                />
                <span>
                  <strong>Weekdays only</strong>
                  <small
                    >Schedule work Monday–Friday. Turn this off if project work
                    can be scheduled on weekends.</small
                  >
                </span>
              </label>
              <div class="field">
                <label for="timezone">Project timezone</label
                ><select id="timezone" v-model="form.timezone" class="select">
                  <option>America/Toronto</option>
                  <option>America/New_York</option>
                  <option>America/Chicago</option>
                  <option>America/Los_Angeles</option>
                </select>
              </div>
            </section>
            <section v-else-if="step === 2" class="flow-section">
              <div>
                <h3>People and organizations</h3>
                <p>
                  Choose the organizations involved. Project access is added
                  explicitly after creation.
                </p>
              </div>
              <div class="field">
                <label for="client-name">Client organization</label
                ><select
                  id="client-name"
                  v-model="form.clientOrganizationId"
                  class="select"
                >
                  <option value="">No client organization yet</option>
                  <option
                    v-for="organization in workspace.organizations"
                    :key="organization.id"
                    :value="organization.id"
                  >
                    {{ organization.name }}
                  </option>
                </select>
                <button
                  class="text-action"
                  type="button"
                  @click="
                    emit('close');
                    router.push('/organizations');
                  "
                >
                  Add a new organization
                </button>
              </div>
              <div class="people-preview">
                <span class="people-preview__icon"
                  ><UsersRound :size="21"
                /></span>
                <div>
                  <strong>VIPdesk delivery team</strong>
                  <p>
                    You will be the project owner. Invite teammates and client
                    participants after the project is created.
                  </p>
                </div>
              </div>
            </section>
            <section v-else-if="step === 3" class="flow-section">
              <div>
                <h3>Choose a starting point</h3>
                <p>Start with a clean plan or bring in a reusable structure.</p>
              </div>
              <label class="starting-card"
                ><input
                  v-model="form.startingPoint"
                  type="radio"
                  value="blank"
                /><span class="starting-card__icon"
                  ><LayoutTemplate :size="22" /></span
                ><span
                  ><strong>Standard sections</strong
                  ><small
                    >Start with
                    {{ STANDARD_PROJECT_SECTIONS.length }} workstreams,
                    including Workforce management and Recruiting, then add
                    tasks and milestones.</small
                  ></span
                ></label
              >
              <label
                v-for="template in workspace.templates"
                :key="template.id"
                class="starting-card"
              >
                <input
                  v-model="form.startingPoint"
                  type="radio"
                  :value="template.id"
                /><span class="starting-card__icon"
                  ><LayoutTemplate :size="22" /></span
                ><span
                  ><strong>{{ template.name }}</strong
                  ><small>{{
                    template.sectionCount !== undefined &&
                    template.taskCount !== undefined
                      ? `${template.sectionCount} sections · ${template.taskCount} tasks · owners and delivery progress excluded`
                      : template.description ||
                        "Use the reviewed sections, tasks, and dependencies without owners or delivery progress."
                  }}</small></span
                >
              </label>
              <p v-if="!workspace.templates.length" class="flow-helper">
                No reusable templates have been published yet.
              </p>
            </section>
            <section v-else class="flow-section">
              <div>
                <h3>Review the project</h3>
                <p>Confirm the schedule and starting access before creation.</p>
              </div>
              <dl class="review-list">
                <div>
                  <dt>Project</dt>
                  <dd>{{ form.name }}</dd>
                </div>
                <div>
                  <dt>Client</dt>
                  <dd>
                    {{
                      selectedOrganization?.name || "No client organization yet"
                    }}
                  </dd>
                </div>
                <div>
                  <dt>Schedule</dt>
                  <dd>
                    {{ form.startDate }} to {{ form.targetEndDate }} ·
                    {{ form.weekdaysOnly ? "Monday–Friday" : "Every day" }}
                  </dd>
                </div>
                <div>
                  <dt>Starting point</dt>
                  <dd>
                    {{
                      form.startingPoint === "blank"
                        ? `${STANDARD_PROJECT_SECTIONS.length} standard sections`
                        : `${selectedTemplate?.name || "Selected template"}${
                            selectedTemplate?.sectionCount !== undefined &&
                            selectedTemplate?.taskCount !== undefined
                              ? ` · ${selectedTemplate.sectionCount} sections and ${selectedTemplate.taskCount} tasks`
                              : ""
                          }`
                    }}
                  </dd>
                </div>
              </dl>
              <div class="review-note">
                New work is available to project participants by default. Mark
                individual work items as Internal when they should remain
                visible only to eligible VIPdesk project participants.
                <template v-if="form.startingPoint !== 'blank'">
                  The selected template copies sections, task hierarchy,
                  descriptions, relative timing, dependencies, forms, and
                  checklists. Owners, comments, files, answers, risks, progress,
                  and delivery history are cleared.
                </template>
              </div>
            </section>
          </div>
        </div>
        <footer class="flow__footer">
          <button
            v-if="step > 1"
            class="button button--secondary"
            type="button"
            @click="step--"
          >
            <ChevronLeft :size="16" /> Back</button
          ><span></span
          ><button
            v-if="step < 4"
            class="button button--primary"
            type="button"
            :disabled="!canAdvance"
            @click="step++"
          >
            Next: {{ steps[step] }} <ChevronRight :size="16" /></button
          ><button
            v-else
            class="button button--primary"
            type="button"
            :disabled="saving || !descriptionValid"
            @click="create"
          >
            {{ saving ? "Creating project…" : "Create project" }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.flow-layer {
  position: fixed;
  inset: 0;
  z-index: 110;
  display: grid;
  place-items: center;
  padding: 24px;
}
.flow-scrim {
  position: absolute;
  inset: 0;
  width: 100%;
  background: rgba(8, 27, 46, 0.55);
}
.flow {
  position: relative;
  width: min(880px, 100%);
  height: min(720px, calc(100vh - 48px));
  display: grid;
  grid-template-rows: auto 1fr auto;
  border-radius: 10px;
  overflow: hidden;
  background: white;
  box-shadow: var(--shadow-panel);
}
.flow__header {
  min-height: 80px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid var(--rule);
}
.flow__header p,
.flow-section > div:first-child p {
  margin-top: 4px;
  color: var(--text-soft);
}
.flow__layout {
  min-height: 0;
  display: grid;
  grid-template-columns: 220px 1fr;
}
.flow__steps {
  padding: 20px 14px;
  border-right: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
.flow__steps button {
  width: 100%;
  min-height: 46px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 5px;
  background: transparent;
  color: var(--text-soft);
  text-align: left;
}
.flow__steps button > span {
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  border: 1px solid var(--rule-strong);
  border-radius: 50%;
  background: white;
  font-size: 11px;
  font-weight: 700;
}
.flow__steps button.current {
  color: var(--text);
  background: var(--brand-soft);
  font-weight: 700;
}
.flow__steps button.current > span,
.flow__steps button.done > span {
  border-color: var(--brand);
  color: white;
  background: var(--brand);
}
.flow__body {
  overflow: auto;
}
.flow-error {
  margin: 18px 30px 0;
  padding: 10px 12px;
  border: 1px solid var(--danger);
  border-radius: 5px;
  background: var(--danger-soft);
  color: var(--danger);
}
.flow-section {
  display: grid;
  gap: 20px;
  max-width: 600px;
  padding: 26px 30px 36px;
}
.text-action {
  justify-self: start;
  background: transparent;
  color: var(--shared);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.flow-helper {
  color: var(--text-soft);
  font-size: 12px;
}
.workweek-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--rule);
  border-radius: 6px;
  background: var(--sheet-subtle);
  cursor: pointer;
}
.workweek-option input {
  width: 18px;
  height: 18px;
  margin-top: 1px;
  accent-color: var(--brand);
}
.workweek-option span {
  display: grid;
  gap: 3px;
}
.workweek-option small {
  color: var(--text-soft);
  line-height: 1.4;
}
.people-preview {
  display: flex;
  gap: 12px;
  padding: 14px;
  border: 1px solid var(--rule);
  border-radius: 7px;
}
.people-preview__icon,
.starting-card__icon {
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 6px;
  color: var(--brand-strong);
  background: var(--brand-soft);
}
.people-preview p {
  margin-top: 3px;
  color: var(--text-soft);
}
.starting-card {
  display: grid;
  grid-template-columns: 20px 40px 1fr;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--rule);
  border-radius: 8px;
  cursor: pointer;
}
.starting-card:has(input:checked) {
  border-color: var(--brand);
  background: var(--brand-soft);
}
.starting-card > span:last-child {
  display: grid;
}
.starting-card small {
  margin-top: 3px;
  color: var(--text-soft);
}
.review-list {
  display: grid;
  margin: 0;
  border-top: 1px solid var(--rule);
}
.review-list > div {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid var(--rule);
}
.review-list dt {
  color: var(--text-soft);
}
.review-list dd {
  margin: 0;
  font-weight: 600;
}
.review-note {
  padding: 13px;
  border-radius: 6px;
  color: #164f82;
  background: var(--shared-soft);
}
.flow__footer {
  min-height: 66px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 8px;
  padding: 14px 22px;
  border-top: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
@media (max-width: 700px) {
  .flow-layer {
    padding: 0;
  }
  .flow {
    width: 100%;
    height: 100%;
    max-height: none;
    border-radius: 0;
  }
  .flow__layout {
    grid-template-columns: 1fr;
  }
  .flow__steps {
    display: flex;
    gap: 4px;
    overflow-x: auto;
    padding: 8px 12px;
    border-right: 0;
    border-bottom: 1px solid var(--rule);
  }
  .flow__steps button {
    width: auto;
    min-width: 38px;
    padding: 5px;
  }
  .flow__steps button:not(.current) {
    flex: 0 0 auto;
  }
  .flow__steps button:not(.current) {
    font-size: 0;
  }
  .flow-section {
    padding: 22px 18px 34px;
  }
  .flow__footer {
    padding: 12px;
  }
}
</style>
